import {message} from "antd";
import {useUserStore} from "@/lib/store/user.store";
import {deleteCookie} from "cookies-next";
import {isDev} from "@/lib/util/util";
import {getLoginLink} from "@/lib/util/get-router";

export type RequestCache = "default" | "no-cache" | "reload" | "force-cache" | "only-if-cached" | "no-store";

export interface RequestConfig {
    endpoint: string;
    method?: "GET" | "POST" | "PUT" | "DELETE";
    body?: any;
    headers?: {
        [key: string]: any;
    };
    params?: {
        [key: string]: any;
    };
    baseUrl?: string;
    cache?: RequestCache;
    revalidate?: number;//要按定时间隔重新验证数据，可以使用“fetch”的“next.revalidate”选项来设置资源的缓存生存期（以秒为单位）。
}

export interface RequestResponse<T> {
    code: number;
    message: string;
    data: T;
    timestamp: number;
}

const TIMEOUT = 20000; // 20秒超时
const MAX_RETRIES = 2; // 最大重试次数

async function request<T>(options: RequestConfig): Promise<RequestResponse<T>> {
    let retries = 0;
    
    while (retries <= MAX_RETRIES) {
        try {
            const {
                endpoint,
                method = "GET",
                cache = "force-cache",
                body,
                headers: customHeaders,
                baseUrl,
                params
            } = options;

            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), TIMEOUT);

            const headers: { [key: string]: string } = {"Content-Type": "application/json", ...customHeaders};
            let url = baseUrl ? `${baseUrl}${endpoint}` : `${process.env.NEXT_PUBLIC_API_URL}${endpoint}`;

            if (headers["Content-Type"].includes("multipart/form-data")) {
                delete headers["Content-Type"];
            }

            const config = {
                method,
                headers,
                body: body ? headers["Content-Type"] === "application/json" ? JSON.stringify(body) : body : undefined,
                cache,
                signal: controller.signal
            };

            const query = params ? JSON.parse(JSON.stringify(params)) : {};
            const userInfo = useUserStore?.getState()?.userInfo;
            if (userInfo && userInfo?.access_token) {
                query["access-token"] = userInfo?.access_token;
            }
            if (query) {
                const queryParameters = Object.keys(query).map(key => `${key}=${query[key]}`).join("&");
                if (queryParameters) {
                    url += `?${queryParameters}`;
                }
            }

            const response = await fetch(url, config);
            clearTimeout(timeoutId);

            const contentType = response.headers.get("content-type");
            const isJson = contentType ? contentType.indexOf("application/json") > -1 : false;
            const data: RequestResponse<T> = isJson ? await response.json() : await response.blob();

            if (response.ok && data.code === 200) {
                return data;
            } else if (data.code === 401) {
                if (typeof window !== "undefined") {
                    message.error({
                        content: "登录已过期，请重新登录",
                        key: "auth_invalid"
                    });
                    if (!isDev) {
                        useUserStore.getState().removeUserInfo;
                        deleteCookie("__userStore");
                        window.location.href = getLoginLink();
                    }
                }
                return data;
            } else {
                return data;
            }
        } catch (err: any) {
            retries++;
            if (retries > MAX_RETRIES) {
                console.error(`请求失败，已重试${MAX_RETRIES}次:`, err.message);
                message.error({
                    content: "网络请求失败，请稍后重试",
                    key: "request_failed"
                });
                return Promise.reject(err);
            }
            // 等待一段时间后重试
            await new Promise(resolve => setTimeout(resolve, 1000 * retries));
        }
    }
    
    // 添加默认返回值，确保函数总是返回一个值
    throw new Error("请求失败，超过最大重试次数");
}



export default request;
